import React from 'react'
import PropTypes from 'prop-types'
import { Wallet02, Lock01, Gift01, ArrowUpRight } from '@untitled-ui/icons-react'
import { MyButton } from '@interstellar-component'

import { formatRp, formatLongDate } from '../../wallet.format'

/** One "label — value" line in the card's right column. */
function StatLine({ icon, label, value }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="flex items-center gap-2 text-sm-regular text-white/80">
        {icon}
        {label}
      </span>
      <span className="text-sm-semibold text-white">{value}</span>
    </div>
  )
}

StatLine.propTypes = {
  icon: PropTypes.node,
  label: PropTypes.string.isRequired,
  value: PropTypes.node.isRequired,
}
StatLine.defaultProps = { icon: null }

/**
 * Right card — the Rupiah wallet (BR-SALDO).
 *
 * The headline figure is `balance_idr`, which is SPENDABLE saldo: money on hold
 * has already left it (the disjoint balance/held model). Held is therefore shown
 * as a separate line rather than folded in — a company seeing one number that
 * silently includes reserved money would over-commit against it.
 */
function SaldoCard({ wallet, onTopup, canAct }) {
  const balance = wallet?.balance_idr
  const held = wallet?.held_idr
  const estimated = wallet?.estimated_requests
  const freeQuota = wallet?.free_quota
  const expiresAt = formatLongDate(wallet?.expires_at)

  return (
    <div className="flex w-full flex-col overflow-hidden rounded-xl bg-blue-dark/600 shadow-sm lg:flex-1">
      <div className="flex flex-col gap-4 p-4 lg:flex-row lg:gap-0">
        {/* ── Balance ────────────────────────────────────────────────────── */}
        <div className="flex flex-col gap-4 lg:w-[300px] lg:shrink-0 lg:pr-4">
          <div className="flex items-center gap-4">
            <span className="flex size-10 items-center justify-center rounded-lg bg-white/15 text-white">
              <Wallet02 className="size-5" />
            </span>
            <h3 className="text-2xl font-semibold text-white">Saldo</h3>
          </div>

          <div className="rounded-lg bg-white/10 px-4 py-3">
            <p className="text-2xl font-semibold text-white">
              {balance != null ? formatRp(balance) : '—'}
            </p>
            <p className="mt-1 text-xs-regular text-white/70">
              {/* null = the company has no resolvable tarif, so the estimate is
                  genuinely unknown. Never print "0 enquiry" for that.

                  "sudah termasuk PPN" is a statement about SPENDING, not about
                  how the figure was grossed up: PPN is settled once on the
                  top-up invoice, and consuming saldo for an enquiry is never
                  taxed again. So the whole balance is usable and the estimate
                  below is the real number of enquiries, not a pre-tax one. */}
              {estimated != null ? (
                <>
                  About <span className="text-xs-semibold text-white">{estimated}</span>{' '}
                  {estimated === 1 ? 'enquiry' : 'enquiries'}
                  <br />
                  <span className="text-white/60">PPN included</span>
                </>
              ) : (
                'Enquiry estimate not available yet'
              )}
            </p>
          </div>
        </div>

        {/* ── Stats + actions ────────────────────────────────────────────── */}
        <div className="flex flex-1 flex-col justify-between gap-4 border-t border-white/20 pt-4 lg:border-l lg:border-t-0 lg:pl-6 lg:pt-0">
          <div className="flex flex-col gap-3">
            <StatLine
              icon={<Lock01 className="size-4" />}
              label="On hold"
              value={held != null ? formatRp(held) : '—'}
            />
            <StatLine
              icon={<Gift01 className="size-4" />}
              label="Free CR quota"
              value={freeQuota != null ? `${freeQuota}x` : '—'}
            />
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3">
            {expiresAt ? (
              <span className="inline-flex items-center rounded-full bg-white/15 px-2.5 py-0.5 text-xs-medium text-white">
                Expires {expiresAt}
              </span>
            ) : (
              <span />
            )}
            <MyButton
              color="secondary"
              variant="outlined"
              size="sm"
              type="button"
              disabled={!canAct}
              onClick={onTopup}
            >
              <span className="flex items-center gap-1.5 text-sm-semibold">
                Top up saldo
                <ArrowUpRight className="size-4" />
              </span>
            </MyButton>
          </div>
        </div>
      </div>
    </div>
  )
}

SaldoCard.propTypes = {
  wallet: PropTypes.shape({
    balance_idr: PropTypes.number,
    held_idr: PropTypes.number,
    estimated_requests: PropTypes.number,
    free_quota: PropTypes.number,
    expires_at: PropTypes.string,
  }),
  onTopup: PropTypes.func,
  canAct: PropTypes.bool,
}

SaldoCard.defaultProps = {
  wallet: null,
  onTopup: null,
  canAct: false,
}

export default SaldoCard
